import React, { useEffect, useState } from "react";
import { Button, Box, Container, Paper, Typography, IconButton, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCreditNotes, deleteCreditNote } from "../services/creditNoteService";
import { useStoreInfo } from "../../../hooks/useCompanyInfo";
import { downloadCreditNotePDF } from "../utils/creditNoteTemplateUtils";
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';

const ListCreditNotesPage = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { storeInfo } = useStoreInfo();

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  const fetchCreditNotes = async () => {
    setLoading(true);
    try {
      const data = await getCreditNotes();
      setCreditNotes(data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this credit note?')) {
      try {
        await deleteCreditNote(id);
        setCreditNotes((prev) => prev.filter((note) => note.Id !== id));
      } catch (error) {
        setError('Failed to delete credit note');
      }
    }
  };

  const handleDownload = async (creditNote) => {
    if (!storeInfo) {
      setError("Store information not loaded. Please refresh the page.");
      return;
    }

    try {
      setDownloadingId(creditNote.Id);
      setError(null);
      await downloadCreditNotePDF(creditNote, storeInfo, t);
    } catch (err) {
      setError('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    { field: "Id", headerName: "ID", flex: 0.5, minWidth: 80 },
    { field: "CreditNoteNumber", headerName: "Credit Note #", flex: 1.5, minWidth: 180 },
    { 
      field: "CreditNoteDate", 
      headerName: "Date", 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const date = params.row.CreditNoteDate;
        return date ? new Date(date).toLocaleDateString() : '-';
      }
    },
    { 
      field: "Reason", 
      headerName: "Reason", 
      flex: 1.5, 
      minWidth: 200,
      renderCell: (params) => params.row.Reason || '-'
    },
    { 
      field: "Total", 
      headerName: "Total Amount", 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        // Calculate total from line items
        const lineItems = params.row.LineItems || [];
        const total = lineItems.reduce((sum, item) => {
          const itemTotal = (item.Quantity || 0) * (item.UnitPrice || 0);
          const vat = itemTotal * ((item.VAT || 0) / 100);
          return sum + itemTotal + vat;
        }, 0);
        return `€${total.toFixed(2)}`;
      }
    },
    { 
      field: "PeppolStatus", 
      headerName: "Peppol Status", 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const status = params.row.PeppolStatus || 'Draft';
        let bg = '';
        switch (status.toLowerCase()) {
          case 'sent':
            bg = '#388e3c';
            break;
          case 'failed':
            bg = '#d32f2f';
            break;
          case 'pending':
            bg = '#f57c00';
            break;
          case 'draft':
          default:
            bg = '#1976d2';
            break;
        }
        return (
          <span
            style={{
              color: '#fff',
              background: bg,
              borderRadius: 10,
              padding: '4px 12px',
              fontWeight: 500,
              fontSize: 12,
              display: 'inline-block',
              textAlign: 'center',
            }}
          >
            {status}
          </span>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/sales/credit-notes/${params.row.Id}`)}
            sx={{
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
              },
            }}
            title="View Details"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDownload(params.row)}
            disabled={downloadingId === params.row.Id || !storeInfo}
            sx={{
              color: '#10b981',
              '&:hover': {
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
              },
            }}
            title="Download PDF"
          >
            {downloadingId === params.row.Id ? (
              <CircularProgress size={20} />
            ) : (
              <DownloadIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.Id)}
            sx={{
              color: '#ef4444',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CreditCardIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Credit Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your credit notes
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/sales/credit-notes/add')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.2s',
              },
            }}
          >
            Add Credit Note
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <ReusableDataGrid
          rows={creditNotes}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
          checkboxSelection
        />
      </Paper>
    </Container>
  );
};

export default ListCreditNotesPage;